/**
 * ChestOpenModal.jsx - Shows chest contents before collecting
 *
 * Flow:
 * 1. Confirm phase - Shows what the chest COULD drop (drop table preview)
 * 2. Opening phase - Animation when user confirms
 * 3. Revealed phase - Shows actual rolled loot
 *
 * User must confirm opening before loot is rolled.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { DUNGEON_RESOURCES } from '../../data/dungeon/resources';
import { ALL_ITEMS, getRandomEquipmentForDungeon, ALL_EQUIPMENT } from '../../data/dungeon';
import { getBossUniquesForBoss } from '../../data/dungeon/equipment/bossUniques';
import './ChestOpenModal.css';

// Rarity colors for equipment display
const RARITY_COLORS = {
  common: '#c8bca8',
  uncommon: '#80b858',
  rare: '#6080a8',
  epic: '#806890',
  legendary: '#dab050',
  mythic: '#ff6b9d',
};

// Rarity display names with proper casing
const RARITY_NAMES = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
  mythic: 'Mythic',
};

// Helper to roll rarity from weighted table
const rollRarityFromWeights = (weights) => {
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  let roll = Math.random() * totalWeight;
  for (const [rarity, weight] of Object.entries(weights)) {
    roll -= weight;
    if (roll <= 0) return rarity;
  }
  return Object.keys(weights)[0];
};

// Calculate percentage chances from weights
const calculatePercentages = (weights) => {
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  return Object.entries(weights).map(([rarity, weight]) => ({
    rarity,
    percent: Math.round((weight / totalWeight) * 100),
  }));
};

// Generate loot from chest (preview version - doesn't add to inventory)
const generateChestLoot = (chest) => {
  if (!chest || !chest.dropTable) return [];

  const dropTable = chest.dropTable;
  const dungeonLevel = chest.dungeonLevel || 10;
  const loot = [];

  // Check if this is a boss chest with unique equipment
  const isBossChest = chest.bossId || chest.defeatedBossId;
  const bossId = chest.bossId || chest.defeatedBossId;
  const uniqueEquipment = bossId ? getBossUniquesForBoss(bossId) : [];
  const uniqueDropChance = chest.uniqueDropChance || 0.10;

  // Calculate total drops
  let totalDrops = chest.guaranteedDrops || 1;
  const maxBonus = chest.maxBonusDrops || 2;
  const bonusChance = chest.bonusDropChance || 0.3;

  for (let i = 0; i < maxBonus; i++) {
    if (Math.random() < bonusChance) totalDrops++;
  }

  // Track which unique items have already dropped (no duplicates in same chest)
  const droppedUniqueIds = new Set();

  // Generate each drop
  for (let i = 0; i < totalDrops; i++) {
    // Roll category
    const categoryRoll = Math.random() * 100;
    let category;
    if (categoryRoll < dropTable.equipment) {
      category = 'equipment';
    } else if (categoryRoll < dropTable.equipment + dropTable.material) {
      category = 'material';
    } else {
      category = 'consumable';
    }

    if (category === 'equipment' && dropTable.equipmentRarity) {
      // For boss chests, first check if we should drop a unique item
      let equipment = null;
      let isUniqueDrop = false;

      if (isBossChest && uniqueEquipment.length > 0 && Math.random() < uniqueDropChance) {
        // Try to drop a unique item (that hasn't already dropped)
        const availableUniques = uniqueEquipment.filter(u => !droppedUniqueIds.has(u.id));
        if (availableUniques.length > 0) {
          equipment = availableUniques[Math.floor(Math.random() * availableUniques.length)];
          droppedUniqueIds.add(equipment.id);
          isUniqueDrop = true;
        }
      }

      // If no unique drop, generate regular equipment
      if (!equipment) {
        const rarity = rollRarityFromWeights(dropTable.equipmentRarity);
        equipment = getRandomEquipmentForDungeon(rarity, dungeonLevel);
      }

      if (equipment) {
        const quality = 0.5 + Math.random() * 0.75;
        loot.push({
          type: 'equipment',
          item: {
            ...equipment,
            instanceId: `${equipment.id}_preview_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            quality,
            isBossUnique: isUniqueDrop,
          },
        });
      }
    } else if (category === 'material' && dropTable.materials?.length > 0) {
      // Generate material
      const materialId = dropTable.materials[Math.floor(Math.random() * dropTable.materials.length)];
      const material = DUNGEON_RESOURCES[materialId];
      if (material) {
        const qty = Math.floor(Math.random() * 3) + 1;
        // Check if already in loot, if so add to quantity
        const existing = loot.find(l => l.type === 'material' && l.item.id === materialId);
        if (existing) {
          existing.quantity += qty;
        } else {
          loot.push({
            type: 'material',
            item: material,
            quantity: qty,
          });
        }
      }
    } else if (category === 'consumable' && dropTable.consumables?.length > 0) {
      // Generate consumable
      const consumableId = dropTable.consumables[Math.floor(Math.random() * dropTable.consumables.length)];
      const consumable = ALL_ITEMS[consumableId];
      if (consumable) {
        const qty = Math.floor(Math.random() * 2) + 1;
        // Check if already in loot
        const existing = loot.find(l => l.type === 'consumable' && l.item.id === consumableId);
        if (existing) {
          existing.quantity += qty;
        } else {
          loot.push({
            type: 'consumable',
            item: consumable,
            quantity: qty,
          });
        }
      }
    }
  }

  return loot;
};

export function ChestOpenModal({ chest, onClose, onCollect }) {
  const [phase, setPhase] = useState('confirm'); // 'confirm' | 'opening' | 'revealed'
  const [loot, setLoot] = useState([]);

  // Reset to confirm phase when chest changes
  useEffect(() => {
    if (chest) {
      setPhase('confirm');
      setLoot([]);
    }
  }, [chest]);

  // Handle confirming to open the chest
  const handleConfirmOpen = () => {
    const generatedLoot = generateChestLoot(chest);
    setLoot(generatedLoot);
    setPhase('opening');
    // Auto-transition to revealed after animation
    setTimeout(() => setPhase('revealed'), 800);
  };

  // Count equipment, materials, consumables
  const lootSummary = useMemo(() => {
    const equipment = loot.filter(l => l.type === 'equipment');
    const materials = loot.filter(l => l.type === 'material');
    const consumables = loot.filter(l => l.type === 'consumable');
    return { equipment, materials, consumables };
  }, [loot]);

  const handleCollect = () => {
    onCollect(loot);
    onClose();
  };

  if (!chest) return null;

  // Check if this is a boss chest
  const isBossChest = chest.bossId || chest.defeatedBossId;
  const bossName = chest.defeatedBossName;

  return (
    <div className="com-overlay" onClick={onClose}>
      <div className={`com-modal ${isBossChest ? 'boss-chest' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`com-header rarity-${chest.rarity || 'common'} ${isBossChest ? 'boss-header' : ''}`}>
          <span className="com-icon">{chest.icon}</span>
          <div className="com-title-group">
            <h2>{chest.name}</h2>
            {bossName && <span className="com-boss-name">from {bossName}</span>}
          </div>
          <span className="com-tier">{isBossChest ? 'Boss' : `Tier ${chest.tier || '?'}`}</span>
        </div>

        {/* Confirm Phase - Show what chest COULD contain */}
        {phase === 'confirm' && chest.dropTable && (
          <div className="com-content">
            {/* Chest Preview */}
            <div className="com-preview-chest">
              <span className="chest-icon-large">{chest.icon}</span>
            </div>

            {/* Drop Chances Section */}
            <div className="com-section">
              <div className="com-section-label">Drop Chances</div>
              <div className="com-drop-chances">
                <div className="com-chance-row">
                  <span className="chance-label">Equipment</span>
                  <span className="chance-value">{chest.dropTable.equipment}%</span>
                </div>
                <div className="com-chance-row">
                  <span className="chance-label">Materials</span>
                  <span className="chance-value">{chest.dropTable.material}%</span>
                </div>
                <div className="com-chance-row">
                  <span className="chance-label">Consumables</span>
                  <span className="chance-value">{chest.dropTable.consumable}%</span>
                </div>
              </div>
            </div>

            {/* Equipment Rarity Chances */}
            {chest.dropTable.equipmentRarity && (
              <div className="com-section">
                <div className="com-section-label">Equipment Rarity</div>
                <div className="com-rarity-chances">
                  {calculatePercentages(chest.dropTable.equipmentRarity).map(({ rarity, percent }) => (
                    <div
                      key={rarity}
                      className={`com-rarity-bar rarity-${rarity}`}
                      style={{ '--rarity-color': RARITY_COLORS[rarity] }}
                    >
                      <span className="rarity-name">{RARITY_NAMES[rarity]}</span>
                      <span className="rarity-percent">{percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Guaranteed Drops Info */}
            <div className="com-section">
              <div className="com-section-label">Guaranteed</div>
              <div className="com-guaranteed-info">
                <span className="guaranteed-text">
                  {chest.guaranteedDrops} item{chest.guaranteedDrops > 1 ? 's' : ''} guaranteed
                  {chest.maxBonusDrops > 0 && (
                    <span className="bonus-text"> (+{chest.maxBonusDrops} bonus possible)</span>
                  )}
                </span>
              </div>
            </div>

            {/* Unique Equipment Preview (for boss chests) */}
            {isBossChest && chest.dropTable?.uniqueEquipment?.length > 0 && (
              <div className="com-section">
                <div className="com-section-label">
                  <span className="unique-label">Unique Drops</span>
                  <span className="unique-chance">({Math.round((chest.uniqueDropChance || 0.1) * 100)}% chance)</span>
                </div>
                <div className="com-unique-grid">
                  {chest.dropTable.uniqueEquipment.slice(0, 3).map(equipId => {
                    const equip = ALL_EQUIPMENT[equipId];
                    if (!equip) return null;
                    return (
                      <div
                        key={equipId}
                        className={`com-unique-item rarity-${equip.rarity}`}
                        style={{ '--rarity-color': RARITY_COLORS[equip.rarity] }}
                      >
                        <span className="unique-icon">{equip.icon}</span>
                        <span className="unique-name">{equip.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Opening Animation */}
        {phase === 'opening' && (
          <div className="com-opening">
            <div className="com-chest-animate">
              <span className="chest-icon-large">{chest.icon}</span>
            </div>
            <div className="com-opening-text">Opening...</div>
          </div>
        )}

        {/* Revealed Loot */}
        {phase === 'revealed' && (
          <div className="com-content">
            {/* Equipment Section */}
            {lootSummary.equipment.length > 0 && (
              <div className="com-section">
                <div className="com-section-label">Equipment</div>
                <div className="com-equipment-grid">
                  {lootSummary.equipment.map((drop, idx) => (
                    <div
                      key={idx}
                      className={`com-equipment-item rarity-${drop.item.rarity} ${drop.item.isBossUnique ? 'boss-unique' : ''}`}
                      style={{ borderColor: RARITY_COLORS[drop.item.rarity] }}
                    >
                      <span className="equip-icon">{drop.item.icon}</span>
                      <div className="equip-info">
                        <div className="equip-name-row">
                          <span className="equip-name">{drop.item.name}</span>
                          {drop.item.isBossUnique && <span className="unique-tag">UNIQUE</span>}
                        </div>
                        <span className="equip-quality">{Math.round(drop.item.quality * 100)}% Quality</span>
                      </div>
                      <span className="equip-rarity">{drop.item.rarity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Materials Section */}
            {lootSummary.materials.length > 0 && (
              <div className="com-section">
                <div className="com-section-label">Materials</div>
                <div className="com-items-grid">
                  {lootSummary.materials.map((drop, idx) => (
                    <div
                      key={idx}
                      className={`com-item rarity-${drop.item.rarity || 'common'}`}
                    >
                      <span className="item-icon">{drop.item.icon}</span>
                      <span className="item-qty">x{drop.quantity}</span>
                      <span className="item-name">{drop.item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Consumables Section */}
            {lootSummary.consumables.length > 0 && (
              <div className="com-section">
                <div className="com-section-label">Consumables</div>
                <div className="com-items-grid">
                  {lootSummary.consumables.map((drop, idx) => (
                    <div
                      key={idx}
                      className={`com-item rarity-${drop.item.rarity || 'common'}`}
                    >
                      <span className="item-icon">{drop.item.icon}</span>
                      <span className="item-qty">x{drop.quantity}</span>
                      <span className="item-name">{drop.item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {loot.length === 0 && (
              <div className="com-empty">
                <span>The chest was empty...</span>
              </div>
            )}
          </div>
        )}

        {/* Footer Buttons */}
        <div className="com-footer">
          {phase === 'confirm' && (
            <>
              <button
                className="com-cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="com-open-btn"
                onClick={handleConfirmOpen}
              >
                Open Chest
              </button>
            </>
          )}
          {phase === 'opening' && (
            <button
              className="com-collect-btn"
              disabled
            >
              Opening...
            </button>
          )}
          {phase === 'revealed' && (
            <button
              className="com-collect-btn ready"
              onClick={handleCollect}
            >
              Collect Loot
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChestOpenModal;
